import has from 'lodash/has';
import trimEnd from 'lodash/trimEnd';
import Decimal from 'decimal.js';

import appSettings from '../config/settings';
import { types as sdkTypes } from '../util/sdkLoader';

const { subUnitDivisors } = appSettings;
const { Money } = sdkTypes;

// https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Global_Objects/Number/MAX_SAFE_INTEGER
// https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Global_Objects/Number/MIN_SAFE_INTEGER
// https://stackoverflow.com/questions/26380364/why-is-number-max-safe-integer-9-007-199-254-740-991-and-not-9-007-199-254-740-9
export const MIN_SAFE_INTEGER = Number.MIN_SAFE_INTEGER || -1 * (2 ** 53 - 1);
export const MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER || 2 ** 53 - 1;

export const isSafeNumber = decimalValue => {
  if (!(decimalValue instanceof Decimal)) {
    throw new Error('Value must be a Decimal');
  }
  return decimalValue.gte(MIN_SAFE_INTEGER) && decimalValue.lte(MAX_SAFE_INTEGER);
};

// Get the minor unit divisor for the given currency
export const unitDivisor = currency => {
  if (!has(subUnitDivisors, currency)) {
    throw new Error(
      `No minor unit divisor defined for currency: ${currency} in currencySettings.js`
    );
  }
  return subUnitDivisors[currency];
};

////////// Currency manipulation in string format //////////

/**
 * Ensures that the given string uses only dots or commas
 * e.g. ensureSeparator('9999999,99', false) // => '9999999.99'
 *
 * @param {String} str - string to be formatted
 *
 * @return {String} converted string
 */
export const ensureSeparator = (str, useComma = false) => {
  if (typeof str !== 'string') {
    throw new TypeError('Parameter must be a string');
  }
  return useComma ? str.replace(/\./g, ',') : str.replace(/,/g, '.');
};

/**
 * Ensures that the given string uses only dots
 * (e.g. JavaScript floats use dots)
 *
 * @param {String} str - string to be formatted
 *
 * @return {String} converted string
 */
export const ensureDotSeparator = str => {
  return ensureSeparator(str, false);
};

/**
 * Convert string to Decimal object (from Decimal.js math library)
 * Handles both dots and commas as decimal separators
 *
 * @param {String} str - string to be converted
 *
 * @return {Decimal} numeral value
 */
export const convertToDecimal = str => {
  const dotFormattedStr = ensureDotSeparator(str);
  return new Decimal(dotFormattedStr);
};

/**
 * Converts Decimal value to a string (from Decimal.js math library)
 *
 * @param {Decimal|Number|String} decimalValue
 *
 * @param {boolean} useComma - optional.
 * Specify if return value should use comma as separator
 *
 * @return {String} converted value
 */
export const convertDecimalToString = (decimalValue, useComma = false) => {
  const d = new Decimal(decimalValue);
  return ensureSeparator(d.toString(), useComma);
};

// Divisor can be positive value given as Decimal, Number, or String
const convertDivisorToDecimal = divisor => {
  try {
    const divisorAsDecimal = new Decimal(divisor);
    if (divisorAsDecimal.isNegative()) {
      throw new Error(`Parameter (${divisor}) must be a positive number.`);
    }
    return divisorAsDecimal;
  } catch (e) {
    throw new Error(`Parameter (${divisor}) must present a number.`, e);
  }
};

/**
 * Limits value to sub-unit precision: "1.4567" -> "1.45"
 * Useful in input fields so this doesn't use rounding.
 *
 * @param {String} inputString - positive number presentation.
 *
 * @param {Decimal|Number|String} subUnitDivisor - should be something that can be converted to
 * Decimal. (This is a ratio between currency's main unit and sub units.)
 *
 * @param {boolean} useComma - optional.
 * Specify if return value should use comma as separator
 *
 * @return {String} truncated value
 */
export const truncateToSubUnitPrecision = (inputString, subUnitDivisor, useComma = false) => {
  const subUnitDivisorAsDecimal = convertDivisorToDecimal(subUnitDivisor);

  // '10,' should be passed through, but that format is not supported as valid number
  const trimmed = trimEnd(inputString, useComma ? ',' : '.');
  // create another instance and check if value is convertable
  const value = convertToDecimal(trimmed, useComma);

  if (value.isNegative()) {
    throw new Error(`Parameter (${inputString}) must be a positive number.`);
  }

  // Amount is always counted in subunits
  // E.g. $10 => 1000¢
  const amount = value.times(subUnitDivisorAsDecimal);

  if (!isSafeNumber(amount)) {
    throw new Error(
      `Cannot represent money minor unit value ${amount.toString()} safely as a number`
    );
  }

  // Amount must be integer
  // We don't deal with subunit fragments like 1000.345¢
  if (amount.isInteger()) {
    // accepted strings: '9', '9,' '9.' '9,99'
    const decimalCount2 = value.toFixed(2);
    const decimalPrecisionMax2 =
      decimalCount2.length >= inputString.length ? inputString : value.toFixed(2);
    return ensureSeparator(decimalPrecisionMax2, useComma);
  } else {
    // truncate strings ('9.999' => '9.99')
    const truncated = amount.truncated().dividedBy(subUnitDivisorAsDecimal);
    return convertDecimalToString(truncated, useComma);
  }
};

////////// Currency - Money helpers //////////

/**
 * Converts given value to sub unit value and returns it as a number
 *
 * @param {Number|String} value
 *
 * @param {Decimal|Number|String} subUnitDivisor - should be something that can be converted to
 * Decimal. (This is a ratio between currency's main unit and sub units.)
 *
 * @param {boolean} useComma - optional.
 * Specify if return value should use comma as separator
 *
 * @return {number} converted value
 */
export const convertUnitToSubUnit = (value, subUnitDivisor, useComma = false) => {
  const subUnitDivisorAsDecimal = convertDivisorToDecimal(subUnitDivisor);

  if (!(typeof value === 'string' || typeof value === 'number')) {
    throw new TypeError('Value must be either number or string');
  }

  const val = typeof value === 'string' ? convertToDecimal(value, useComma) : new Decimal(value);
  const amount = val.times(subUnitDivisorAsDecimal);

  if (!isSafeNumber(amount)) {
    throw new Error(
      `Cannot represent money minor unit value ${amount.toString()} safely as a number`
    );
  } else if (amount.isInteger()) {
    return amount.toNumber();
  } else {
    throw new Error(`value must divisible by ${subUnitDivisor}`);
  }
};

const isNumber = value => {
  return typeof value === 'number' && !isNaN(value);
};

/* eslint-disable no-underscore-dangle */
// Detect if the given value is a goog.math.Long object
// See: https://google.github.io/closure-library/api/goog.math.Long.html
const isGoogleMathLong = value => {
  return typeof value === 'object' && isNumber(value.low_) && isNumber(value.high_);
};
/* eslint-enable no-underscore-dangle */

/**
 * Convert Money to a number
 *
 * @param {Money} value
 *
 * @return {Number} converted value
 */
export const convertMoneyToNumber = value => {
  if (!(value instanceof Money)) {
    throw new Error('Value must be a Money type');
  }
  const subUnitDivisorAsDecimal = convertDivisorToDecimal(unitDivisor(value.currency));
  let amount;

  if (isGoogleMathLong(value.amount)) {
    // TODO: temporarily also handle goog.math.Long values created by
    // the Transit tooling in the Sharetribe JS SDK. This should be
    // removed when the value.amount will be a proper Decimal type.

    // eslint-disable-next-line no-console
    console.warn('goog.math.Long value in money amount:', value.amount, value.amount.toString());

    amount = new Decimal(value.amount.toString());
  } else {
    amount = new Decimal(value.amount);
  }

  if (!isSafeNumber(amount)) {
    throw new Error(
      `Cannot represent money minor unit value ${amount.toString()} safely as a number`
    );
  }

  return amount.dividedBy(subUnitDivisorAsDecimal).toNumber();
};

/**
 * Format the given money to a string
 *
 * @param {Object} intl
 * @param {Money} value
 *
 * @return {String} formatted money value
 */
export const formatMoney = (intl, value) => {
  if (!(value instanceof Money)) {
    throw new Error('Value must be a Money type');
  }
  const valueAsNumber = convertMoneyToNumber(value);

  // See: https://github.com/yahoo/react-intl/wiki/API#formatnumber
  const numberFormatOptions = {
    style: 'currency',
    currency: value.currency,
    currencyDisplay: 'symbol',
    useGrouping: true,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  return intl.formatNumber(valueAsNumber, numberFormatOptions);
};

/**
 * Format the given major-unit string value as currency. E.g. "10" -> "$10".
 *
 * NOTE: This function should not be used with listing prices or other Money type.
 * This can be used with price filters and other components that doesn't send Money types to API.
 *
 * @param {Object} intl
 * @param {String} value
 *
 * @return {String} formatted money value
 */
export const formatCurrencyMajorUnit = (intl, currency, valueWithoutSubunits) => {
  const valueAsNumber = new Decimal(valueWithoutSubunits).toNumber();

  // See: https://github.com/yahoo/react-intl/wiki/API#formatnumber
  const numberFormatOptions = {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };

  return intl.formatNumber(valueAsNumber, numberFormatOptions);
};

const allCurrencies = [
  { key: 'EUR', label: '€', labelPopup: '€ EUR', maxLimit: 500, countryCode: 'EU' },
  { key: 'USD', label: '$', labelPopup: '$ USD', maxLimit: 500, countryCode: 'US' },
  { key: 'GBP', label: '£', labelPopup: '£ GBP', maxLimit: 500, countryCode: 'GB' },
  { key: 'HKD', label: 'HK$', labelPopup: 'HK$ HKD', maxLimit: 4000, countryCode: 'HK' },
  { key: 'SGD', label: 'SGD', labelPopup: 'S$ SGD', maxLimit: 750, countryCode: 'SG' },
  { key: 'AUD', label: 'A$', labelPopup: 'AUD AUD', maxLimit: 750, countryCode: 'AU' },
  { key: 'CAD', label: 'CA$', labelPopup: 'CDN$ CAD', maxLimit: 750, countryCode: 'CA' },
  { key: 'PLN', label: 'PLN', labelPopup: 'zł PLN', maxLimit: 2000, countryCode: 'PL' },
  { key: 'CHF', label: 'CHF', labelPopup: 'CHF CHF', maxLimit: 500, countryCode: 'CH' },
  { key: 'SEK', label: 'SEK', labelPopup: 'kr SEK', maxLimit: 5000, countryCode: 'SE' },
  { key: 'DKK', label: 'DKK', labelPopup: 'kr DKK', maxLimit: 3500, countryCode: 'DK' },
  { key: 'JPY', label: '¥', labelPopup: 'JPY JPY', maxLimit: 80000, countryCode: 'JP' },
  { key: 'KRW', label: '₩', labelPopup: '₩ KRW', maxLimit: 750000, countryCode: 'KR' },
];

const currenciesByLocale = {
  en: 'EUR', // default currency in Europe
  en_GB: 'GBP',
  en_CA: 'CAD',
  en_AU: 'AUD',
  en_SG: 'SGD',
  en_HK: 'HKD',
  pl: 'PLN',
  fr: 'EUR',
  de: 'EUR',
  it: 'EUR',
  es: 'EUR',
  ch: 'CHF',
  se: 'SEK',
  dk: 'DKK',
  jp: 'JPY',
  kr: 'KRW',
};

const currenciesForKlarna = [
  { country: 'Australia', countryCode: 'AU', currency: 'AUD' },
  { country: 'Canada', countryCode: 'CA', currency: 'CAD' },
  { country: 'Switzerland', countryCode: 'CH', currency: 'CHF' },
  { country: 'Poland', countryCode: 'PL', currency: 'PLN' },
  { country: 'Denmark', countryCode: 'DK', currency: 'DKK' },
  { country: 'United Kingdom', countryCode: 'GB', currency: 'GBP' },
  { country: 'Norway', countryCode: 'NO', currency: 'NOK' },
  { country: 'Sweden', countryCode: 'SE', currency: 'SEK' },
  { country: 'United States', countryCode: 'US', currency: 'USD' },
  { country: 'European Union', countryCode: 'EU', currency: 'EUR' },
];

const defaultCurrency = process.env.REACT_APP_DEFAULT_CURRENCY || 'EUR';
if (!allCurrencies.some(currency => currency.key === defaultCurrency)) {
  throw new Error(`Default currency "${defaultCurrency}" is not included in allCurrencies.`);
}

const configurationCurrencies = process.env.REACT_APP_AVAILABLE_CURRENCIES || defaultCurrency;

const configurationCurrenciesParsed = configurationCurrencies
  .split(',')
  .map(currency => currency.trim())
  .filter(Boolean);

const currencyOptions = allCurrencies.filter(currency =>
  configurationCurrenciesParsed.includes(currency.key)
);

if (currencyOptions.length === 0) {
  currencyOptions.push(allCurrencies.find(currency => currency.key === defaultCurrency));
}

const currencySwitchEnabled = process.env.REACT_APP_CURRENCY_SWITCH_ENABLED === 'true';

const showCurrencyInFront = locale => locale !== 'de';

export {
  defaultCurrency,
  currencyOptions,
  currencySwitchEnabled,
  currenciesByLocale,
  currenciesForKlarna,
  showCurrencyInFront,
};
